import React, { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { Check, Truck, Zap, Banknote, CreditCard } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useStore, money } from "@/context/StoreContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

function loadRzp() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const STEPS = ["Information", "Delivery", "Payment", "Confirmation"];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const { settings } = useStore();
  const nav = useNavigate();
  const loc = useLocation();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [info, setInfo] = useState({ name: user?.name || "", email: user?.email || "", phone: "",
    house: "", street: "", area: "", city: "", state: "", pincode: "", country: "India" });
  const [shipMethod, setShipMethod] = useState("standard");
  const [payMethod, setPayMethod] = useState("cod");

  const cod = settings?.cod || {};
  const rzpEnabled = settings?.razorpay?.enabled;
  const threshold = settings?.free_shipping_threshold || 999;
  const freeShip = subtotal >= threshold;
  const shipping = freeShip ? 0 : (shipMethod === "express" ? (settings?.express_shipping || 149) : (settings?.standard_shipping || 79));
  const tax = Math.round(subtotal * (settings?.gst_percent || 5) / 100);
  const codFee = payMethod === "cod" ? (cod.fee || 0) : 0;
  const total = subtotal + shipping + tax + codFee;

  if (items.length === 0 && !submitted) return <Navigate to="/shop" replace />;

  const validInfo = info.name && info.email && info.phone && info.house && info.street && info.city && info.state && info.pincode.length === 6;

  const placeOrder = async () => {
    setBusy(true);
    const payload = {
      items: items.map((i) => ({ product_id: i.product_id, sku: i.sku, quantity: i.quantity })),
      customer: { name: info.name, email: info.email, phone: info.phone },
      address: info, shipping_method: shipMethod, payment_method: payMethod,
      coupon_code: loc.state?.coupon || null,
    };
    try {
      const { data } = await api.post("/checkout", payload);
      if (data.payment_method === "cod") {
        setSubmitted(true); clear();
        nav(`/order-confirmation/${data.order.id}`, { state: { order: data.order } });
        return;
      }
      // online
      const ok = await loadRzp();
      if (!ok) { toast.error("Could not load payment gateway"); setBusy(false); return; }
      const rzp = new window.Razorpay({
        key: data.razorpay_key_id, amount: data.amount, currency: data.currency,
        name: "SHAROA", description: `Order ${data.order_number}`, order_id: data.razorpay_order_id,
        prefill: { name: info.name, email: info.email, contact: info.phone },
        theme: { color: "#D88A8A" },
        handler: async (resp) => {
          try {
            const v = await api.post("/checkout/verify", {
              order_id: data.order_id, razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature,
            });
            setSubmitted(true); clear();
            nav(`/order-confirmation/${v.data.order.id}`, { state: { order: v.data.order } });
          } catch (e) { toast.error(apiErr(e)); setBusy(false); }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.open();
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
      {/* stepper */}
      <div className="flex items-center justify-between mb-10 max-w-2xl mx-auto">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center gap-1.5">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-mono ${i <= step ? "bg-sharoa-rose text-white" : "bg-sharoa-line text-sharoa-muted"}`}>
                {i < step ? <Check size={14} /> : `0${i + 1}`}
              </div>
              <span className={`text-[10px] uppercase tracking-widest ${i <= step ? "text-sharoa-charcoal" : "text-sharoa-muted"}`}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${i < step ? "bg-sharoa-rose" : "bg-sharoa-line"}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          {step === 0 && (
            <div className="space-y-4">
              <h2 className="font-serif text-2xl">Contact & Address</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input label="Full Name" v={info.name} on={(x) => setInfo({ ...info, name: x })} testid="co-name" />
                <Input label="Email" v={info.email} on={(x) => setInfo({ ...info, email: x })} testid="co-email" />
                <Input label="Mobile" v={info.phone} on={(x) => setInfo({ ...info, phone: x })} testid="co-phone" />
                <Input label="House / Flat" v={info.house} on={(x) => setInfo({ ...info, house: x })} testid="co-house" />
                <Input label="Street" v={info.street} on={(x) => setInfo({ ...info, street: x })} testid="co-street" />
                <Input label="Area" v={info.area} on={(x) => setInfo({ ...info, area: x })} testid="co-area" />
                <Input label="City" v={info.city} on={(x) => setInfo({ ...info, city: x })} testid="co-city" />
                <Input label="State" v={info.state} on={(x) => setInfo({ ...info, state: x })} testid="co-state" />
                <Input label="PIN Code" v={info.pincode} on={(x) => setInfo({ ...info, pincode: x })} testid="co-pincode" />
                <Input label="Country" v={info.country} on={(x) => setInfo({ ...info, country: x })} testid="co-country" />
              </div>
              <button data-testid="co-to-delivery" disabled={!validInfo} onClick={() => setStep(1)} className="bg-sharoa-rose disabled:opacity-40 text-white text-xs uppercase tracking-widest px-8 py-4">Continue to Delivery</button>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="font-serif text-2xl">Delivery Method</h2>
              {[["standard", "Standard Delivery", "4–6 business days", settings?.standard_shipping || 79, Truck],
                ["express", "Express Delivery", "2–3 business days", settings?.express_shipping || 149, Zap]].map(([id, t, d, f, Icon]) => (
                <button key={id} data-testid={`ship-${id}`} onClick={() => setShipMethod(id)}
                  className={`w-full flex items-center gap-3 border p-4 text-left ${shipMethod === id ? "border-sharoa-rose bg-sharoa-pale" : "border-sharoa-line"}`}>
                  <Icon size={20} className="text-sharoa-rose" />
                  <div className="flex-1"><p className="font-medium text-sm">{t}</p><p className="text-xs text-sharoa-muted">{d}</p></div>
                  <span className="font-mono text-sm">{freeShip ? "FREE" : money(f)}</span>
                </button>
              ))}
              <div className="flex gap-3">
                <button onClick={() => setStep(0)} className="border border-sharoa-line text-xs uppercase tracking-widest px-6 py-4">Back</button>
                <button data-testid="co-to-payment" onClick={() => setStep(2)} className="bg-sharoa-rose text-white text-xs uppercase tracking-widest px-8 py-4">Continue to Payment</button>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-serif text-2xl">Payment</h2>
              {rzpEnabled && (
                <button data-testid="pay-online" onClick={() => setPayMethod("online")}
                  className={`w-full flex items-center gap-3 border p-4 text-left ${payMethod === "online" ? "border-sharoa-rose bg-sharoa-pale" : "border-sharoa-line"}`}>
                  <CreditCard size={20} className="text-sharoa-rose" />
                  <div className="flex-1"><p className="font-medium text-sm">Pay Online</p><p className="text-xs text-sharoa-muted">UPI · Cards · Net Banking · Wallets (Razorpay)</p></div>
                </button>
              )}
              {cod.enabled && (
                <button data-testid="pay-cod" onClick={() => setPayMethod("cod")}
                  className={`w-full flex items-center gap-3 border p-4 text-left ${payMethod === "cod" ? "border-sharoa-rose bg-sharoa-pale" : "border-sharoa-line"}`}>
                  <Banknote size={20} className="text-sharoa-rose" />
                  <div className="flex-1"><p className="font-medium text-sm">Cash on Delivery</p><p className="text-xs text-sharoa-muted">Pay when your order is delivered.</p></div>
                  <span className="font-mono text-sm">{codFee ? money(cod.fee) + " fee" : "FREE"}</span>
                </button>
              )}
              {!rzpEnabled && <p className="text-xs text-sharoa-muted">Online payment is being set up. Cash on Delivery is available now ♡</p>}
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="border border-sharoa-line text-xs uppercase tracking-widest px-6 py-4">Back</button>
                <button data-testid="place-order-btn" disabled={busy} onClick={placeOrder} className="bg-sharoa-rose disabled:opacity-50 text-white text-xs uppercase tracking-widest px-8 py-4 flex-1">
                  {busy ? "Processing…" : payMethod === "online" ? `Pay ${money(total)}` : `Place COD Order · ${money(total)}`}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* summary */}
        <div className="bg-sharoa-ivory p-6 h-fit sticky top-24">
          <h3 className="font-serif text-lg mb-4">Order Summary</h3>
          <div className="space-y-3 max-h-56 overflow-y-auto mb-4">
            {items.map((it) => (
              <div key={it.sku} className="flex gap-3 text-sm">
                <img src={it.image} alt="" className="w-12 h-16 object-cover bg-white" />
                <div className="flex-1"><p className="font-medium leading-tight">{it.name}</p><p className="text-xs text-sharoa-muted">{it.size}·{it.color}·{it.quantity}</p></div>
                <span className="font-mono text-xs">{money(it.price * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-2 text-sm border-t border-sharoa-line pt-3">
            <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{money(subtotal)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span className="font-mono">{shipping === 0 ? "FREE" : money(shipping)}</span></div>
            <div className="flex justify-between"><span>Tax</span><span className="font-mono">{money(tax)}</span></div>
            {codFee > 0 && <div className="flex justify-between"><span>COD Fee</span><span className="font-mono">{money(codFee)}</span></div>}
            <div className="flex justify-between font-serif text-lg border-t border-sharoa-line pt-2"><span>Total</span><span className="font-mono">{money(total)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Input({ label, v, on, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-sharoa-muted">{label}</span>
      <input data-testid={testid} value={v} onChange={(e) => on(e.target.value)}
        className="w-full border border-sharoa-line bg-white px-3 py-2.5 text-sm mt-1 outline-none focus:border-sharoa-rose" />
    </label>
  );
}
